/**
 * DashboardsPanel — the shared two-pane "Dashboards" view used on both My Page
 * (personal) and Close Portal team pages (team).
 *
 * Layout: a left-hand list of QuickSight (FGBSBI) dashboards ranked by usage,
 * and a right-hand pane that embeds the selected dashboard.
 *
 * The ranked list is loaded from the generic TopItems table (written by the
 * qs_top_dashboards rollup job) via getTopDashboards. Selecting a dashboard
 * fetches a fresh, short-lived QuickSight embed URL on demand (from the stored
 * id/region/account) and renders it in an iframe.
 *
 * Props:
 *   scope        {"user" | "team"}  who the ranking is for (drives copy).
 *   teamName     {string}           team label, used in copy when scope==="team".
 *   ldapGroup    {string}           team's LDAP group id (team scope only),
 *                                   used as the DynamoDB scope id.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Grid,
  Header,
  Icon,
  SpaceBetween,
} from "@amzn/awsui-components-react";

import "../index.scss";
import "./homepage.scss";
import { getTopDashboards } from "src/utilities/CloseAPIs";
import { getQSDashboardEmbedLinksSync } from "src/Ateam-BI/Templates/Utils/AppActivityUtilsAppsyncPlus";

// The embed fills most of the viewport height so a full QuickSight dashboard
// has room to render (rather than a cramped fixed box). Used for both the
// iframe and the loading/empty/error placeholders so the pane height is stable.
const EMBED_HEIGHT = "85vh";
const EMBED_PANE_STYLE = {
  minHeight: EMBED_HEIGHT,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

// Fallback window (in days) shown in the intro copy before the record loads,
// on fetch error, or when the record omits window_days. Kept in sync with the
// qs_top_dashboards job's WINDOW_DAYS so the copy never shows a stale window.
const DEFAULT_WINDOW_DAYS = 90;

/**
 * @param {object} props
 * @param {"user"|"team"} props.scope
 * @param {string} [props.teamName]  team label (team scope only), for copy.
 * @param {string} [props.ldapGroup] the team's LDAP group id (team scope only);
 *                                   used as the DynamoDB scope id.
 */
function DashboardsPanel({ scope = "user", teamName, ldapGroup }) {
  const userId = useSelector((globalState) => globalState.auth.userId);

  const [selectedId, setSelectedId] = useState(null);
  const [dashboards, setDashboards] = useState([]);
  const [windowDays, setWindowDays] = useState(null);
  const [loading, setLoading] = useState(true);

  // Embed state for the right-hand pane (the selected dashboard).
  const [embedUrl, setEmbedUrl] = useState(null);
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedError, setEmbedError] = useState(false);

  // Collapsible left list (mirrors the Close Portal Resources panel): when
  // collapsed, the list is hidden and the embed pane spans the full width.
  const [leftMenuCollapsed, setLeftMenuCollapsed] = useState(false);
  const gridDefinition = leftMenuCollapsed
    ? [{ colspan: 12 }]
    : [{ colspan: 3 }, { colspan: 9 }];

  // Resolve the DynamoDB scope: USER#<alias> for My Page, TEAM#<ldap_group> for
  // a Close Portal team page.
  const scopeType = scope === "team" ? "TEAM" : "USER";
  const scopeId = scope === "team" ? ldapGroup : userId;

  useEffect(() => {
    let cancelled = false;
    if (!scopeId) {
      setDashboards([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    getTopDashboards(scopeType, scopeId)
      .then((record) => {
        if (cancelled) return;
        // Generic TopItems payload: the ranked list lives in `items`.
        setDashboards(Array.isArray(record?.items) ? record.items : []);
        setWindowDays(
          Number.isFinite(record?.window_days) ? record.window_days : null
        );
      })
      .catch(() => {
        if (!cancelled) {
          setDashboards([]);
          setWindowDays(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scopeType, scopeId]);

  const introText = useMemo(() => {
    // Window text tracks the record's window_days; the count is fixed at 10
    // (the job's TOP_N) regardless of how many entries actually came back.
    const windowText = `${windowDays || DEFAULT_WINDOW_DAYS} days`;
    if (scope === "team") {
      return `Top 10 QuickSight dashboards used across ${
        teamName ? `the ${teamName} team` : "your team"
      }, ranked by usage over the last ${windowText}. Select a dashboard from the left to open it.`;
    }
    return `Your top 10 used QuickSight dashboards, ranked by your personal usage over the last ${windowText}. Select a dashboard from the left to open it.`;
  }, [scope, teamName, windowDays]);

  const selected = useMemo(
    () => dashboards.find((d) => d.id === selectedId) || null,
    [dashboards, selectedId]
  );

  // When the selected dashboard changes, fetch a fresh QuickSight embed URL.
  // The URLs are short-lived + signed, so they're generated on demand from the
  // stored id/region/account rather than persisted by the rollup job.
  useEffect(() => {
    let cancelled = false;
    setEmbedUrl(null);
    setEmbedError(false);
    if (!selected) {
      setEmbedLoading(false);
      return undefined;
    }
    setEmbedLoading(true);
    // Stored entry provides account ("FGBSBI"), region, and id — matching the
    // helper's (aws_account, dashboard_region, dashboard_id) signature.
    getQSDashboardEmbedLinksSync(
      selected.account || "FGBSBI",
      selected.region || "us-east-1",
      selected.id
    )
      .then((response) => {
        if (cancelled) return;
        if (response?.url) {
          setEmbedUrl(response.url);
        } else {
          setEmbedError(true);
        }
      })
      .catch(() => {
        if (!cancelled) setEmbedError(true);
      })
      .finally(() => {
        if (!cancelled) setEmbedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected]);

  return (
    // Narrow left list, wide right pane. When the left list is collapsed the
    // grid becomes a single full-width column (mirrors Close Portal Resources).
    <Grid gridDefinition={gridDefinition}>
      {/* Left: ranked dashboard list (hidden when collapsed) */}
      {!leftMenuCollapsed && (
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <span
                  style={{ cursor: "pointer" }}
                  onClick={() => setLeftMenuCollapsed(true)}
                >
                  <Icon name="angle-left-double" size="medium" variant="normal" />
                </span>
              }
            >
              Dashboards
            </Header>
          }
          disableContentPaddings={false}
        >
          {loading ? (
            <Box padding={{ vertical: "l" }} textAlign="center" color="text-body-secondary">
              Loading dashboards…
            </Box>
          ) : dashboards.length === 0 ? (
            <Box padding={{ vertical: "xxl" }} textAlign="center" color="inherit">
              <b>No dashboards yet</b>
              <Box variant="p" color="text-body-secondary" padding={{ top: "xs" }}>
                {scope === "team"
                  ? "This team's most-used dashboards will appear here."
                  : "Your most-used dashboards will appear here."}
              </Box>
            </Box>
          ) : (
            <SpaceBetween size="s">
              {dashboards.map((d) => (
                <div
                  key={d.id}
                  onClick={() => setSelectedId(d.id)}
                  style={{
                    cursor: "pointer",
                    color: d.id === selectedId ? "#0972d3" : "#0f141a",
                    fontWeight: d.id === selectedId ? 700 : 400,
                  }}
                >
                  {d.name}
                </div>
              ))}
            </SpaceBetween>
          )}
        </Container>
      )}

      {/* Right: compact intro banner over one large embed container. */}
      <SpaceBetween size="s">
        {/* Expand toggle, shown only when the left list is collapsed. */}
        {leftMenuCollapsed && (
          <span
            style={{ cursor: "pointer" }}
            onClick={() => setLeftMenuCollapsed(false)}
          >
            <Icon name="angle-right-double" size="medium" variant="normal" />
          </span>
        )}

        <Container>
          <Box
            display="inline-block"
            color="text-body-secondary"
            fontSize="body-s"
          >
            {introText}
          </Box>
        </Container>

        <Container disableContentPaddings={!!embedUrl && !embedError}>
          {!selected ? (
            <div style={EMBED_PANE_STYLE}>
              <Box textAlign="center" color="text-body-secondary">
                Select a dashboard from the left to open it.
              </Box>
            </div>
          ) : embedLoading ? (
            <div style={EMBED_PANE_STYLE}>
              <Box textAlign="center" color="text-body-secondary">
                Loading {selected.name}…
              </Box>
            </div>
          ) : embedError || !embedUrl ? (
            <div style={EMBED_PANE_STYLE}>
              <Box textAlign="center" color="text-body-secondary">
                <b>{selected.name}</b>
                <Box variant="p" padding={{ top: "xs" }}>
                  Unable to load this dashboard. Please try again.
                </Box>
              </Box>
            </div>
          ) : (
            // Fill most of the viewport so the dashboard has room to render
            // fully (matches the large Close Portal embed). A viewport-relative
            // height avoids depending on ancestor heights, so it works the same
            // on both My Page and team pages.
            <iframe
              title={selected.name}
              src={embedUrl}
              style={{ width: "100%", height: EMBED_HEIGHT, border: "none" }}
            />
          )}
        </Container>
      </SpaceBetween>
    </Grid>
  );
}

export default DashboardsPanel;
